import type { ReceiptStatus } from "@opensea/graphql"
import { useNumberFormatter } from "@opensea/i18n"
import {
  ImageStack,
  Toast,
  ToastAvatar,
  ToastContent,
  ToastDescription,
  ToastErrorIcon,
  ToastSide,
  ToastSuccessIcon,
  ToastTitle,
} from "@opensea/ui-kit"
import { Offer } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { Avatar } from "../../../components/Avatar"
import { useRenderImage } from "../../../providers/RenderImageProvider"

type CreateOfferToastProps = {
  imageUrls: string[]
  price: { unit: number; symbol: string }
  success: boolean
  status: ReceiptStatus
}

export function CreateOfferToast({
  imageUrls,
  price,
  success,
  status,
}: CreateOfferToastProps) {
  const t = useTranslations("wallet.CreateOfferToast")
  const { renderImage } = useRenderImage()
  const format = useNumberFormatter()

  const badge = (
    <Avatar.Badge>
      <Offer size={12} />
    </Avatar.Badge>
  )

  return (
    <Toast>
      {imageUrls.length === 1 ? (
        <ToastAvatar
          badge={badge}
          renderImage={renderImage}
          src={imageUrls[0] || ""}
        />
      ) : (
        <ImageStack
          overrides={{
            Avatar: {
              position: {
                center: {
                  className: "rounded",
                  renderImage,
                  badge,
                },
                right: {
                  className: "rounded",
                },
              },
            },
          }}
          size={44}
          srcs={imageUrls.slice(0, 2).map(imageUrl => imageUrl || "")}
        />
      )}
      <ToastContent>
        {status === "SUCCESS" || success ? (
          <>
            <ToastTitle>{t("title", { count: imageUrls.length })}</ToastTitle>
            {imageUrls.length === 1 ? (
              <ToastDescription>
                {format(price.unit, { display: "full", suffix: price.symbol })}
              </ToastDescription>
            ) : null}
          </>
        ) : (
          <ToastTitle>{t("failedTitle")}</ToastTitle>
        )}
      </ToastContent>
      <ToastSide>
        {status === "SUCCESS" || success ? (
          <ToastSuccessIcon />
        ) : (
          <ToastErrorIcon />
        )}
      </ToastSide>
    </Toast>
  )
}
